/**
 * Dictionary Page - Specialized Terminology Dictionary
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getDictionaryTerms,
  createDictionaryTerm,
  updateDictionaryTerm,
  deleteDictionaryTerm,
  autoTranslateDictionaryTerm,
  getCategories,
} from '../services/api';
import {
  DictionaryTerm,
  DictionaryTermCreate,
  DictionaryTermUpdate,
} from '../types';

type ExplanationLanguage = 'vietnamese' | 'chinese' | 'english';

const DictionaryPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [terms, setTerms] = useState<DictionaryTerm[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [explanationLang, setExplanationLang] = useState<ExplanationLanguage>('vietnamese');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  
  // Form states
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTerm, setEditingTerm] = useState<DictionaryTerm | null>(null);
  const [formData, setFormData] = useState<DictionaryTermCreate>({
    english: '',
    chinese: '',
    vietnamese: '',
    explanation_vietnamese: '',
    explanation_chinese: '',
    explanation_english: '',
    category: '',
  });
  const [isAutoTranslating, setIsAutoTranslating] = useState(false);

  // Load terms and categories
  useEffect(() => {
    loadTerms();
    loadCategories();
  }, [searchQuery, selectedCategory, currentPage, itemsPerPage]);
  
  // Reset to page 1 when search or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory]);

  const loadTerms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const skip = (currentPage - 1) * itemsPerPage;
      const data = await getDictionaryTerms(
        searchQuery || undefined,
        selectedCategory || undefined,
        skip,
        itemsPerPage
      );
      setTerms(data.items);
      setTotalItems(data.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load terms');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadTerms();
  };

  const handleAdd = async () => {
    // Validate: at least one language field must be filled
    if (!formData.english && !formData.chinese && !formData.vietnamese) {
      setError('Please fill in at least one language field (English, Chinese, or Vietnamese)');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const newTerm = await createDictionaryTerm(formData);
      setTerms([newTerm, ...terms]);
      resetForm();
      setShowAddForm(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add term');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingTerm) return;
    if (!isAuthenticated) {
      setError('Please login to update terms');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const updateData: DictionaryTermUpdate = {
        english: formData.english || undefined,
        chinese: formData.chinese || undefined,
        vietnamese: formData.vietnamese || undefined,
        explanation_vietnamese: formData.explanation_vietnamese || undefined,
        explanation_chinese: formData.explanation_chinese || undefined,
        explanation_english: formData.explanation_english || undefined,
        category: formData.category || undefined,
      };
      await updateDictionaryTerm(editingTerm.id, updateData);
      // Reload terms to refresh pagination
      await loadTerms();
      resetForm();
      setEditingTerm(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update term');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!isAuthenticated) {
      setError('Please login to delete terms');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this term?')) return;

    setIsLoading(true);
    setError(null);
    try {
      await deleteDictionaryTerm(id);
      // Reload terms to refresh pagination
      await loadTerms();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete term');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoTranslate = async () => {
    // Check which fields are filled
    const hasEnglish = !!formData.english.trim();
    const hasChinese = !!formData.chinese.trim();
    const hasVietnamese = !!formData.vietnamese.trim();

    if (!hasEnglish && !hasChinese && !hasVietnamese) {
      setError('Please fill in at least one language field before auto-translating');
      return;
    }

    setIsAutoTranslating(true);
    setError(null);
    try {
      const result = await autoTranslateDictionaryTerm({
        english: formData.english || undefined,
        chinese: formData.chinese || undefined,
        vietnamese: formData.vietnamese || undefined,
        category: formData.category || undefined,
      });

      // Update form with translations (only fill empty fields)
      setFormData({
        english: formData.english || result.english,
        chinese: formData.chinese || result.chinese,
        vietnamese: formData.vietnamese || result.vietnamese,
        explanation_vietnamese: formData.explanation_vietnamese || result.explanation_vietnamese || '',
        explanation_chinese: formData.explanation_chinese || result.explanation_chinese || '',
        explanation_english: formData.explanation_english || result.explanation_english || '',
        category: formData.category || '',
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to auto-translate');
    } finally {
      setIsAutoTranslating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      english: '',
      chinese: '',
      vietnamese: '',
      explanation_vietnamese: '',
      explanation_chinese: '',
      explanation_english: '',
      category: '',
    });
  };

  const startEdit = (term: DictionaryTerm) => {
    setEditingTerm(term);
    setFormData({
      english: term.english,
      chinese: term.chinese,
      vietnamese: term.vietnamese,
      explanation_vietnamese: term.explanation_vietnamese || '',
      explanation_chinese: term.explanation_chinese || '',
      explanation_english: term.explanation_english || '',
      category: term.category || '',
    });
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingTerm(null);
    resetForm();
    setShowAddForm(false);
  };

  const getExplanation = (term: DictionaryTerm): string => {
    switch (explanationLang) {
      case 'vietnamese':
        return term.explanation_vietnamese || '-';
      case 'chinese':
        return term.explanation_chinese || '-';
      case 'english':
        return term.explanation_english || '-';
      default:
        return '-';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            Specialized Terminology Dictionary
          </h1>
          <p className="text-blue-100">
            Manage specialized terms in English, Chinese, and Vietnamese
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4 mb-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search terms..."
              className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <select
              value={explanationLang}
              onChange={(e) => setExplanationLang(e.target.value as ExplanationLanguage)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="vietnamese">Explanation: Vietnamese</option>
              <option value="chinese">Explanation: Chinese</option>
              <option value="english">Explanation: English</option>
            </select>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Search
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(!showAddForm);
                if (showAddForm) cancelEdit();
              }}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              {showAddForm ? 'Cancel' : '+ Add Term'}
            </button>
            {!isAuthenticated && (
              <div className="text-sm text-orange-600 font-medium flex items-center">
                Đăng nhập để sử dụng đầy đủ chức năng
              </div>
            )}
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">
              {editingTerm ? 'Edit Term' : 'Add New Term'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  English *
                </label>
                <input
                  type="text"
                  value={formData.english}
                  onChange={(e) => setFormData({ ...formData, english: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chinese (中文) *
                </label>
                <input
                  type="text"
                  value={formData.chinese}
                  onChange={(e) => setFormData({ ...formData, chinese: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vietnamese (Tiếng Việt) *
                </label>
                <input
                  type="text"
                  value={formData.vietnamese}
                  onChange={(e) => setFormData({ ...formData, vietnamese: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., Computer Science, Medicine"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Explanation (Vietnamese)
                </label>
                <textarea
                  value={formData.explanation_vietnamese}
                  onChange={(e) => setFormData({ ...formData, explanation_vietnamese: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Explanation (Chinese)
                </label>
                <textarea
                  value={formData.explanation_chinese}
                  onChange={(e) => setFormData({ ...formData, explanation_chinese: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Explanation (English)
                </label>
                <textarea
                  value={formData.explanation_english}
                  onChange={(e) => setFormData({ ...formData, explanation_english: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <button
                onClick={editingTerm ? handleUpdate : handleAdd}
                disabled={isLoading || !isAuthenticated}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 transition-colors"
                title={!isAuthenticated ? 'Đăng nhập để lưu từ mới' : ''}
              >
                {isLoading ? 'Saving...' : editingTerm ? 'Update' : 'Add'}
              </button>
              <button
                onClick={handleAutoTranslate}
                disabled={isAutoTranslating || (!formData.english && !formData.chinese && !formData.vietnamese)}
                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 transition-colors"
              >
                {isAutoTranslating ? 'Translating...' : '🤖 Auto Translate & Explain'}
              </button>
              {editingTerm && (
                <button
                  onClick={cancelEdit}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              * At least one language field (English, Chinese, or Vietnamese) is required.
              Auto-translate will fill in missing translations and explanations.
            </p>
            {!isAuthenticated && showAddForm && (
              <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>Lưu ý:</strong> Bạn có thể sử dụng chức năng Auto Translate để dịch và giải thích tự động, nhưng cần đăng nhập để lưu từ mới vào từ điển.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Terms Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    English
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    中文
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tiếng Việt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Explanation
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && terms.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : terms.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                      No terms found
                    </td>
                  </tr>
                ) : (
                  terms.map((term) => (
                    <tr key={term.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {term.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{term.english}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{term.chinese}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{term.vietnamese}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {getExplanation(term)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {term.category || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => startEdit(term)}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(term.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalItems > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage * itemsPerPage >= totalItems}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                    <span className="font-medium">{totalItems}</span> results
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 flex items-center gap-2">
                    Items per page:
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="ml-1 border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Page numbers */}
                    {Array.from({ length: Math.min(5, Math.ceil(totalItems / itemsPerPage)) }, (_, i) => {
                      const totalPages = Math.ceil(totalItems / itemsPerPage);
                      let pageNum: number;
                      
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={currentPage * itemsPerPage >= totalItems}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DictionaryPage;

